import _ = require("underscore");
import Mediator = require("../mediator");
import Portal = require("portal-gis");
import Utils = require("../utils");
let log = Utils.log;

type Proxify<T> = {
    [P in keyof T]: string;
};

// bool and num revert to strings (functions and objects remain pure)
function evaluateMyOptions(options: Rhythm.GIS.Dictionary<string>) {
    Object.keys(options)
        .filter(k => typeof options[k] === "string")
        .filter(k => options[k].indexOf("eval(") === 0)
        .forEach(k => {
            options[k] = eval(options[k]);
            switch (typeof options[k]) {
                case "boolean":
                case "number":
                    options[k] = options[k] + "";
                    break;
            }
        });
}

class Command<T extends { in: any; out: any; }> {

    static about = `
This is the base of all other commands.  It provides the command options as a _config_ property that can be extended without modifying the source command.

It introduces support for the "event" option, which can be a comma-separated list of event names.

It introduces the _trigger_ method, which creates "in" and "out" arguments before calling mediator.trigger, once for each event.

`;

    // none    
    static input = {
    };

    // none    
    static output = {
    };

    // none    
    static options = {
        "disabled": {
            "about": "true to prevent the command from executing",
            "domain": "true|false"
        },
        "debug": {
            "about": "hit a breakpoint before executing this command",
        },
        "event": {
            "about": "the event to raise once the command executes",
        },
        "trace": {
            "about": "write the command state to the console window",
        },
    };

    private _config: Rhythm.GIS.Dictionary<string>;

    asOptions<O>(defaults: O) {
        return <Proxify<O>>this.options;
    }

    get options() {
        if (this._config) return this._config;
        this.configuration.Options.Values.forEach(v => v.value = Utils.translate(v.value));
        let options = Utils.asDictionary(this.configuration.Options.Values);
        let classOptions = this.constructor["options"];

        classOptions && Object.keys(classOptions)
            .filter(k => undefined !== classOptions[k].default && undefined === options[k])
            .forEach(k => options[k] = classOptions[k].default);

        evaluateMyOptions(options);

        return this._config = options;
    }

    destroy() {
        // perform necessary cleanup (if any)
    }

    constructor(public configuration: Portal.CommandLeaf, public mediator: Mediator, public args: T) {
        mediator.on("destroy-commands", () => this.destroy());
    }

    /**
     * Finds an option or argument value
     * If no args are passed search the options
     * Then do a top-down search on this.args (args, in, out) 
     */
    findOption(name: string) {
        if (name in this.options) {
            // is it an option?
            return this.options[name];
        }
        let args = this.findArg(name, this.args);
        return args && args[name];
    }

    findArg(name: string, args: any) {
        if (typeof args !== "object") return false;
        if (args === null) return false;
        if (name in args) return args;
        return false
            || this.findArg(name, args.options)
            || this.findArg(name, args.args)
            || this.findArg(name, args.out)
            || this.findArg(name, args.in)
            || this.findArg(name, args.controller)
            ;
    }

    execute(args?: T) {
        throw "you must shadow the execute method, be sure to call trigger to complete the process";
    }

    trigger(event?: any, args?: any) {
        _.defaults(this.options);

        switch (arguments.length) {
            case 0:
                args = {};
                event = null;
                break;
            case 1:
                if (typeof event === "string") {
                    args = {};
                } else {
                    args = event;
                    event = null;
                }
                break;
        }

        // did I get it right?
        if (event) {
            if (typeof event !== "string") {
                throw "event name should be the first parameter";
            }
        }

        if (args) {
            if (typeof args !== "object") {
                throw "output args should be the second parameter";
            }
            if (args.in || args.out) {
                throw "trigger should not have in/out attributes";
            }
        }

        // ensure in/out pattern, in => command input, out => command output
        args = {
            in: this,
            out: args || {}
        };

        let events = [];

        if (event) {
            events.push(event);
        } else {
            events.push(this.configuration.id);
            Utils.ifOption(this.options, "event", v => events = Utils.split(v));
        }

        events.forEach(event => {
            log(`command ${this.configuration.id} triggered ${event}`);
            this.mediator.trigger(event, args);
        });
    }

}

export = Command; 